/**
 * The pair a translated page opens on, and its one-tap swap (SEO_BRIEF_11 section 3).
 *
 * /es/traductor-de-audio opens on Spanish → English: the speech's language is Spanish and the
 * target picker (lib/translate-target.ts) has English chosen. The page draws the speech's language
 * above the picker with a Swap button (tools/_translate_picker.html `translate_pair`,
 * `[data-translate-pair]`), and one tap trades the two: English → Spanish. The upload declares the
 * speech's language as a language page's row does (lib/upload-offer.ts `languageNow`), so after a
 * swap it says English.
 *
 * A target picked that is the speech's own language swaps the pair too, so the page never asks for
 * a language translated into itself. The names come from the picker's own options, which the server
 * named in the page's language; the swap is drawn only where the picker offers both languages.
 *
 * Every other page has no pair: `pairSource` answers undefined and the upload sends the row's own
 * language, or none, as before.
 */

import { qs } from './dom.ts';
import { PICKER, TARGET } from './translate-target.ts';

/** The pair's block. */
export const PAIR = '[data-translate-pair]';

/** The speech's language on a page that opens on a pair, else undefined. */
export function pairSource(root: ParentNode = document): string | undefined {
  const value = qs<HTMLElement>(PAIR, root)?.dataset['source'];
  return typeof value === 'string' && TARGET.test(value) ? value : undefined;
}

/** The picker's name for `code`, in the page's language, or undefined when it has no such option. */
function optionName(picker: HTMLSelectElement, code: string): string | undefined {
  for (const option of Array.from(picker.options)) {
    if (option.value === code) {
      const name = (option.textContent ?? '').trim();
      return name === '' ? undefined : name;
    }
  }
  return undefined;
}

/** Wire the Swap button and the picker. Does nothing on a page without a pair. */
export function mountTranslatePair(root: ParentNode = document): void {
  const pair = qs<HTMLElement>(PAIR, root);
  const picker = qs<HTMLSelectElement>(PICKER, root);
  const from = pair ? qs<HTMLElement>('[data-pair-from]', pair) : null;
  const swap = pair ? qs<HTMLButtonElement>('[data-pair-swap]', pair) : null;
  if (!pair || !picker || !from || !swap) return;

  /** Make `code` the speech's language. False, and nothing changed, when the picker lacks it. */
  const setSource = (code: string): boolean => {
    const name = optionName(picker, code);
    if (name === undefined) return false;
    pair.dataset['source'] = code;
    from.textContent = name;
    return true;
  };

  let previous = picker.value;

  swap.addEventListener('click', () => {
    const source = pairSource(root);
    const target = picker.value;
    if (source === undefined || !TARGET.test(target) || source === target) return;
    if (optionName(picker, source) === undefined || !setSource(target)) return;
    picker.value = source;
    previous = source;
    picker.dispatchEvent(new Event('change', { bubbles: true }));
  });

  picker.addEventListener('change', () => {
    if (picker.value === pairSource(root) && previous !== picker.value) setSource(previous);
    previous = picker.value;
  });
}
